// src/pages/StatusManager.jsx
import React, { useEffect, useState } from "react";
import Tag from "../components/Tag";
import axiosBackend from "../api/axiosBackend";
import { DEFAULT_TAG_COLOR } from "utils/tagColor";

// shadcn
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle as DialogTitleUI,
  DialogDescription,
} from "components/ui/dialog";

// icons
import { Plus, ArrowUp, ArrowDown } from "lucide-react";

function StatusManager() {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addColor, setAddColor] = useState(DEFAULT_TAG_COLOR);
  const [addIsDefault, setAddIsDefault] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  const [editStatus, setEditStatus] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState(DEFAULT_TAG_COLOR);
  const [editIsDefault, setEditIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const fetchStatuses = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const response = await axiosBackend.get("/statuses/get");
      setStatuses(response.data || []);
    } catch (error) {
      console.error("Error fetching statuses:", error);
      setErrorMsg("אירעה שגיאה בעת טעינת הסטטוסים");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  const handleAddClick = () => {
    setAddName("");
    setAddColor(DEFAULT_TAG_COLOR);
    setAddIsDefault(false);
    setAddError("");
    setAddOpen(true);
  };

  const handleAddSave = async () => {
    if (!addName.trim()) return;
    setAdding(true);
    setAddError("");
    try {
      const response = await axiosBackend.post("/statuses/add", {
        name: addName.trim(),
        color: addColor,
        isDefault: addIsDefault,
      });
      setStatuses((prev) => [...prev, response.data]);
      setAddOpen(false);
    } catch (error) {
      console.error("Error adding status:", error);
      setAddError("אירעה שגיאה בעת הוספת הסטטוס");
    } finally {
      setAdding(false);
    }
  };

  const handleEditClick = (status) => {
    setEditStatus(status);
    setEditName(status.name);
    setEditColor(status.color || DEFAULT_TAG_COLOR);
    setEditIsDefault(!!status.isDefault);
    setEditError("");
  };

  const handleEditSave = async () => {
    if (!editStatus) return;
    if (!editName.trim()) return;
    setSaving(true);
    setEditError("");
    try {
      const response = await axiosBackend.put(`/statuses/${editStatus._id}`, {
        name: editName.trim(),
        color: editColor,
        isDefault: editIsDefault,
      });
      setStatuses((prev) =>
        prev.map((s) => {
          if (s._id === response.data._id) return response.data;
          // isDefault is exclusive server-side; reflect that locally too
          return editIsDefault ? { ...s, isDefault: false } : s;
        })
      );
      setEditStatus(null);
    } catch (error) {
      console.error("Error updating status:", error);
      setEditError("אירעה שגיאה בעת עדכון הסטטוס");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (status) => {
    if (!window.confirm(`למחוק את הסטטוס "${status.name}"?`)) return;
    setErrorMsg("");
    try {
      await axiosBackend.delete(`/statuses/${status._id}`);
      setStatuses((prev) => prev.filter((s) => s._id !== status._id));
    } catch (error) {
      const message = error?.response?.data?.message || "אירעה שגיאה בעת מחיקת הסטטוס";
      setErrorMsg(message);
    }
  };

  const reorder = async (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= statuses.length) return;
    const reordered = [...statuses];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setStatuses(reordered); // optimistic

    try {
      const response = await axiosBackend.put("/statuses/reorder", {
        orderedIds: reordered.map((s) => s._id),
      });
      setStatuses(response.data || reordered);
    } catch (error) {
      console.error("Error reordering statuses:", error);
      fetchStatuses(); // resync on failure
    }
  };

  return (
    <div dir="rtl">
      <Card className="mx-auto max-w-5xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-2xl font-bold text-foreground">ניהול סטטוסים</CardTitle>
          <Button size="icon" className="h-10 w-10 rounded-full shrink-0" onClick={handleAddClick} aria-label="הוספת סטטוס" title="הוספת סטטוס">
            <Plus className="h-5 w-5" />
          </Button>
        </CardHeader>

        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">טוען סטטוסים…</p>
          ) : errorMsg ? (
            <p className="text-sm text-destructive">{errorMsg}</p>
          ) : statuses.length === 0 ? (
            <p className="text-sm text-muted-foreground">לא נמצאו סטטוסים.</p>
          ) : (
            <div className="space-y-2">
              {statuses.map((status, index) => (
                <div
                  key={status._id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-2"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Tag name={status.name} color={status.color} />
                    {status.isDefault && (
                      <span className="text-xs font-medium text-muted-foreground">ברירת מחדל</span>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => reorder(index, index - 1)}
                      disabled={index === 0}
                      aria-label="הזז למעלה"
                      title="הזז למעלה"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => reorder(index, index + 1)}
                      disabled={index === statuses.length - 1}
                      aria-label="הזז למטה"
                      title="הזז למטה"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => handleEditClick(status)}>
                      עריכה
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(status)}>
                      מחיקה
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitleUI>הוספת סטטוס</DialogTitleUI>
            <DialogDescription>הזן שם ובחר צבע, ולחץ הוסף.</DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-3">
            <Input
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder="שם סטטוס"
              autoFocus
            />

            <div className="flex items-center gap-3">
              <Label htmlFor="add-status-color" className="text-muted-foreground">
                צבע
              </Label>
              <input
                id="add-status-color"
                type="color"
                value={addColor}
                onChange={(e) => setAddColor(e.target.value)}
                className="h-9 w-14 cursor-pointer rounded-md border bg-background p-1"
              />
              <Tag name={addName || "תצוגה מקדימה"} color={addColor} />
            </div>

            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={addIsDefault}
                onChange={(e) => setAddIsDefault(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              קבע כסטטוס ברירת מחדל להזמנות חדשות
            </label>

            {addError && <p className="text-sm text-destructive">{addError}</p>}

          </DialogBody>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={adding}>
              ביטול
            </Button>
            <Button onClick={handleAddSave} disabled={adding || !addName.trim()}>
              {adding ? "מוסיף…" : "הוסף"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editStatus} onOpenChange={(open) => !open && setEditStatus(null)}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitleUI>עריכת סטטוס</DialogTitleUI>
            <DialogDescription>עדכן את שם הסטטוס ולחץ שמור.</DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-3">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="שם סטטוס"
              autoFocus
            />

            <div className="flex items-center gap-3">
              <Label htmlFor="edit-status-color" className="text-muted-foreground">
                צבע
              </Label>
              <input
                id="edit-status-color"
                type="color"
                value={editColor}
                onChange={(e) => setEditColor(e.target.value)}
                className="h-9 w-14 cursor-pointer rounded-md border bg-background p-1"
              />
              <Tag name={editName || "תצוגה מקדימה"} color={editColor} />
            </div>

            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={editIsDefault}
                onChange={(e) => setEditIsDefault(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              קבע כסטטוס ברירת מחדל להזמנות חדשות
            </label>

            {editError && <p className="text-sm text-destructive">{editError}</p>}

          </DialogBody>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditStatus(null)} disabled={saving}>
              ביטול
            </Button>
            <Button onClick={handleEditSave} disabled={saving || !editName.trim()}>
              {saving ? "שומר…" : "שמור"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default StatusManager;
